import { Star, Quote, CheckCircle, ExternalLink } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRef, useEffect, useState } from "react";

const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

export const Testimonials = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const testimonials = [
    {
      name: "Matias Delgado",
      text: "Superior roofing solutions are provided by Safeway Roofing & Chimney. Their expert and long-lasting service, which ranges from repairs to complete replacements, gives homeowners peace of mind.",
      rating: 5,
      date: "2 weeks ago"
    },
    {
      name: "Susilawati",
      text: "Safeway Roofing & Chimney provided excellent service. Jimmy and his team were very professional, quick, and thorough when repairing our roof. I couldn't be happier with their work!",
      rating: 5,
      date: "1 month ago"
    },
    {
      name: "Hartadi Prayudha",
      text: "The roof repair from Safeway Roofing & Chimney was quick and professional. Jimmy and his team worked efficiently, and the results were outstanding. I would definitely recommend them!",
      rating: 5,
      date: "3 weeks ago"
    }
  ];

  const avatarColors = [
    "bg-blue-500",
    "bg-emerald-500",
    "bg-amber-500",
    "bg-purple-500",
    "bg-rose-500",
    "bg-cyan-500"
  ];

  const getInitials = (name: string) => 
    name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const getAvatarColor = (name: string) => 
    avatarColors[name.length % avatarColors.length];

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollLeft = container.scrollLeft;
      const cardWidth = container.offsetWidth * 0.85;
      const newIndex = Math.round(scrollLeft / cardWidth);
      setActiveIndex(Math.min(newIndex, testimonials.length - 1));
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [testimonials.length]);

  const scrollToIndex = (index: number) => {
    const container = scrollRef.current;
    if (!container) return;
    const cardWidth = container.offsetWidth * 0.85;
    container.scrollTo({ left: cardWidth * index, behavior: 'smooth' });
  };

  return (
    <section id="testimonials" className="py-20 bg-gradient-to-b from-muted/30 to-background relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-40 h-40 bg-amber-400/10 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-4">
        {/* Header with Google Branding */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-3 bg-card/80 backdrop-blur-sm border border-border/50 rounded-full px-6 py-3 mb-6 shadow-lg">
            <GoogleLogo />
            <span className="font-semibold text-foreground">Google Reviews</span>
            <div className="flex items-center gap-1">
              <span className="text-xl font-bold text-foreground">5.0</span>
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                ))}
              </div>
            </div>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-4">
            What Our Clients Say
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Trusted by homeowners across Long Island for quality roofing services
          </p>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-6 lg:gap-8">
          {testimonials.map((testimonial, index) => (
            <Card 
              key={index} 
              className="group relative border-0 shadow-lg bg-card/80 backdrop-blur-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1 overflow-hidden"
            >
              {/* Quote Decoration */}
              <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
                <Quote className="w-16 h-16 text-primary" />
              </div>
              
              <CardContent className="p-8 relative z-10">
                {/* Header: Avatar + Name + Verified */}
                <div className="flex items-start gap-4 mb-5">
                  <div className={`w-12 h-12 rounded-full ${getAvatarColor(testimonial.name)} flex items-center justify-center text-white font-semibold text-lg shadow-md`}>
                    {getInitials(testimonial.name)}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground text-lg">{testimonial.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex items-center gap-1 text-emerald-600">
                        <CheckCircle className="w-3.5 h-3.5" />
                        <span className="text-xs font-medium">Verified</span>
                      </div>
                      <span className="text-xs text-muted-foreground">• {testimonial.date}</span>
                    </div>
                  </div>
                </div>

                {/* Stars */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                </div>

                {/* Review Text */}
                <p className="text-muted-foreground leading-relaxed mb-5">
                  "{testimonial.text}"
                </p>

                {/* Posted on Google */}
                <div className="flex items-center gap-2 pt-4 border-t border-border/50">
                  <GoogleLogo />
                  <span className="text-sm text-muted-foreground">Posted on Google</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden">
          <div 
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {testimonials.map((testimonial, index) => (
              <Card 
                key={index} 
                className="flex-shrink-0 w-[85vw] snap-center border-0 shadow-lg bg-card/80 backdrop-blur-sm overflow-hidden"
              >
                <CardContent className="p-6 relative">
                  {/* Quote Decoration */}
                  <div className="absolute top-4 right-4 opacity-10">
                    <Quote className="w-12 h-12 text-primary" />
                  </div>
                  
                  {/* Header: Avatar + Name + Verified */}
                  <div className="flex items-start gap-3 mb-4">
                    <div className={`w-11 h-11 rounded-full ${getAvatarColor(testimonial.name)} flex items-center justify-center text-white font-semibold shadow-md`}>
                      {getInitials(testimonial.name)}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground">{testimonial.name}</h4>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="flex items-center gap-1 text-emerald-600">
                          <CheckCircle className="w-3 h-3" />
                          <span className="text-xs font-medium">Verified</span>
                        </div>
                        <span className="text-xs text-muted-foreground">• {testimonial.date}</span>
                      </div>
                    </div>
                  </div>

                  {/* Stars */}
                  <div className="flex items-center gap-0.5 mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>

                  {/* Review Text */}
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    "{testimonial.text}"
                  </p>

                  {/* Posted on Google */}
                  <div className="flex items-center gap-2 pt-3 border-t border-border/50">
                    <GoogleLogo />
                    <span className="text-xs text-muted-foreground">Posted on Google</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination Dots */}
          <div className="flex justify-center gap-2 mt-4">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  activeIndex === index 
                    ? 'bg-primary w-6' 
                    : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                }`}
                aria-label={`Go to review ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12">
          <Button 
            asChild
            className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg"
          >
            <a 
              href="https://g.page/r/safeway-roofing/review" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <GoogleLogo />
              Write a Review
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
          <Button 
            variant="outline"
            asChild
            className="border-border/50 hover:bg-muted/50"
          >
            <a 
              href="https://www.google.com/search?q=safeway+roofing+chimney+reviews" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              See All Reviews
              <ExternalLink className="w-4 h-4" />
            </a>
          </Button>
        </div>

        {/* Trust Stats */}
        <div className="grid grid-cols-3 gap-6 mt-16 pt-10 border-t border-border/30">
          <div className="text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-primary">20+</h3>
            <p className="text-sm md:text-base text-muted-foreground mt-1">Years of Service</p>
          </div>
          <div className="text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-primary">24/7</h3>
            <p className="text-sm md:text-base text-muted-foreground mt-1">Emergency Repairs</p>
          </div>
          <div className="text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-primary">Same-Day</h3>
            <p className="text-sm md:text-base text-muted-foreground mt-1">Service Available</p>
          </div>
        </div>
      </div>
    </section>
  );
};
